<template>
  <Dialog :open="open" @update:open="emit('update:open', $event)">
    <DialogContent class="sm:max-w-2xl">
      <DialogHeader>
        <DialogTitle v-if="step === 'choose'">Create a workflow</DialogTitle>
        <DialogTitle v-else-if="step === 'blank'">New workflow</DialogTitle>
        <DialogTitle v-else>Create from a template</DialogTitle>
        <DialogDescription v-if="step === 'choose'">How would you like to start?</DialogDescription>
        <DialogDescription v-else-if="step === 'blank'"
          >Give your workflow a name and pick a trigger.</DialogDescription
        >
        <DialogDescription v-else>
          Start from a proven workflow. We'll set up the messages, filters, and timing, then you can tweak anything.
        </DialogDescription>
      </DialogHeader>

      <div v-if="step === 'choose'" class="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <button
          type="button"
          class="group flex flex-col gap-3 rounded-xl border border-border bg-card p-5 text-left transition hover:border-indigo-300 hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
          @click="step = 'template'"
        >
          <span class="flex h-10 w-10 items-center justify-center rounded-lg bg-indigo-50">
            <vue-feather type="copy" class="h-5 w-5 text-indigo-600" />
          </span>
          <h3 class="font-semibold leading-tight text-gray-900">Use a template</h3>
          <p class="text-sm leading-snug text-muted-foreground">
            Pick a proven workflow with messages, filters, and timing ready to go.
          </p>
        </button>
        <button
          type="button"
          class="group flex flex-col gap-3 rounded-xl border border-border bg-card p-5 text-left transition hover:border-indigo-300 hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
          @click="step = 'blank'"
        >
          <span class="flex h-10 w-10 items-center justify-center rounded-lg bg-gray-100">
            <vue-feather type="file" class="h-5 w-5 text-gray-600" />
          </span>
          <h3 class="font-semibold leading-tight text-gray-900">Start from scratch</h3>
          <p class="text-sm leading-snug text-muted-foreground">
            Build a blank workflow and add your own steps from the ground up.
          </p>
        </button>
      </div>

      <button
        v-if="step !== 'choose'"
        type="button"
        class="-mb-1 flex items-center gap-1 self-start text-sm font-medium text-muted-foreground transition hover:text-gray-900"
        @click="step = 'choose'"
      >
        <vue-feather type="arrow-left" class="h-4 w-4" />
        Back
      </button>

      <form v-if="step === 'blank'" class="flex flex-col gap-4" @submit.prevent="submitBlank">
        <div class="flex flex-col gap-1.5">
          <Label for="name_input">Name</Label>
          <Input id="name_input" v-model="name" placeholder="Enter a workflow name" />
        </div>
        <div class="flex flex-col gap-1.5">
          <Label for="description_input">Description</Label>
          <Textarea
            id="description_input"
            v-model="description"
            rows="4"
            placeholder="Describe what this workflow will do"
          />
        </div>
        <div class="flex flex-col gap-1.5">
          <Label>Trigger</Label>
          <Select v-model="selectedTriggerOption">
            <SelectTrigger>
              <SelectValue placeholder="Select a trigger" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Candidate Created">Candidate Created</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <DialogFooter>
          <Button type="submit" :disabled="creating">Save</Button>
        </DialogFooter>
      </form>

      <HFetchQuery v-if="step === 'template'" :query="templatesQuery">
        <template #default="{ data: templates }">
          <div v-if="templates.length > SEARCH_THRESHOLD" class="relative">
            <vue-feather type="search" class="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input v-model="search" placeholder="Search templates" class="pl-9" />
          </div>

          <div class="grid max-h-[60vh] grid-cols-1 gap-3 overflow-y-auto pr-1 sm:grid-cols-2">
            <button
              v-for="template in orderedTemplates(templates)"
              :key="template.key"
              type="button"
              :disabled="!!creatingKey || isAdded(template)"
              :title="isAdded(template) ? 'You already have this template' : undefined"
              class="group relative flex flex-col gap-3 rounded-xl border border-border bg-card p-4 text-left transition hover:border-indigo-300 hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:border-border disabled:hover:shadow-none"
              @click="selectTemplate(template.key)"
            >
              <div class="flex items-start justify-between">
                <Badge variant="outline">{{ triggerLabel(template) }}</Badge>
                <vue-feather v-if="isAdded(template)" type="check-circle" class="h-4 w-4 text-emerald-600" />
                <vue-feather
                  v-else-if="creatingKey === template.key"
                  type="loader"
                  class="h-4 w-4 animate-spin text-muted-foreground"
                />
                <vue-feather
                  v-else
                  type="arrow-right"
                  class="h-4 w-4 text-muted-foreground opacity-0 transition group-hover:translate-x-0.5 group-hover:opacity-100"
                />
              </div>

              <div class="flex flex-col gap-1">
                <h3 class="font-semibold leading-tight text-gray-900">{{ template.workflow.name }}</h3>
                <p class="text-sm leading-snug text-muted-foreground">{{ template.workflow.description }}</p>
              </div>

              <div class="mt-auto flex flex-wrap items-center gap-1.5">
                <span
                  v-if="isAdded(template)"
                  class="inline-flex items-center gap-1 text-xs font-medium text-emerald-600"
                >
                  <vue-feather type="check" class="h-3 w-3" />
                  You already have this template
                </span>
                <template v-else>
                  <span
                    v-for="type in actionTypesFor(template)"
                    :key="type"
                    class="flex h-8 w-8 items-center justify-center rounded-lg"
                    :class="actionTypeStyle(type).bg"
                  >
                    <vue-feather
                      :type="actionTypeStyle(type).icon"
                      class="h-4 w-4"
                      :class="actionTypeStyle(type).text"
                    />
                  </span>
                </template>
              </div>
            </button>
          </div>

          <ZeroState v-if="templates.length === 0">There are no templates available yet.</ZeroState>
          <p v-else-if="matching(templates).length === 0" class="py-6 text-center text-sm text-muted-foreground">
            No templates match "{{ search }}".
          </p>
        </template>
      </HFetchQuery>
    </DialogContent>
  </Dialog>
</template>

<script setup lang="ts">
import { ref } from 'vue';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import ZeroState from '@/components/ZeroState.vue';
import HFetchQuery from '@/lib/hfetch/HFetchQuery.vue';
import { useHFetchQuery } from '@/lib/hfetch/useHFetchQuery';
import { Api } from '@/api';
import type { WorkflowTemplate } from '@/api/entity_types/workflow/WorkflowTemplate';
import type {
  WorkflowActionableType,
  WorkflowTriggerDefinitionKey,
} from '@/api/entity_types/workflow/WorkflowDefinition';
import { actionTypeStyle } from '../actionTypeStyle';
import { useCreateFromTemplate } from './useCreateFromTemplate';
import { useNewWorkflow } from './useNewWorkflow';

const props = defineProps<{ open: boolean; existingWorkflowNames: string[] }>();
const emit = defineEmits<{ 'update:open': [value: boolean] }>();

const step = ref<'choose' | 'template' | 'blank'>('choose');

const SEARCH_THRESHOLD = 6;

const TRIGGER_LABELS: Record<WorkflowTriggerDefinitionKey, string> = {
  candidate_created: 'Candidate Created',
  candidate_updated: 'Candidate Updated',
};

const isAdded = (template: WorkflowTemplate) => props.existingWorkflowNames.includes(template.workflow.name);

const actionTypesFor = (template: WorkflowTemplate): WorkflowActionableType[] => {
  const types: WorkflowActionableType[] = [];
  if (template.emails?.length) types.push('WorkflowActionEmail');
  if (template.sms_messages?.length) types.push('WorkflowActionSms');
  if (template.one_off_tasks?.length) types.push('WorkflowActionOneOffTask');
  return types;
};

const triggerLabel = (template: WorkflowTemplate) => TRIGGER_LABELS[template.workflow.trigger_definition_key];

const templatesQuery = useHFetchQuery(() => Api.WorkflowTemplates.listWorkflowTemplates());
const { creatingKey, selectTemplate } = useCreateFromTemplate();

const { name, description, selectedTriggerOption, createWorkflow } = useNewWorkflow();
const creating = ref(false);
const submitBlank = async () => {
  creating.value = true;
  try {
    await createWorkflow();
  } finally {
    creating.value = false;
  }
};

const search = ref('');
const matching = (templates: WorkflowTemplate[]) => {
  const query = search.value.trim().toLowerCase();
  if (!query) return templates;
  return templates.filter(
    template =>
      template.workflow.name.toLowerCase().includes(query) ||
      template.workflow.description.toLowerCase().includes(query)
  );
};

const orderedTemplates = (templates: WorkflowTemplate[]) =>
  [...matching(templates)].sort((a, b) => Number(isAdded(a)) - Number(isAdded(b)));
</script>
